import { Layout, Menu, Space, Spin, Tooltip } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { usePayItemSummarySiderCountsQuery } from 'schema'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useCanView } from '../../../../hooks/useCanView'
import { useSummaryContext } from '../../SummaryContext'
import ConnectionMenuItem from './components/ConnectionMenuItem'
import InternalMenuItem from './components/InternalMenuItem'
import CIMSBadge from 'components/CIMSBadge'
import { ItemType, MenuItemType } from 'antd/lib/menu/hooks/useItems'
import { usePayItemsModuleContext } from '../../../../PayItemsModuleContext'
import {
  Connection,
  Internal,
  Item,
} from '../../../../PayItemsModuleContext/types'
import { useProjectContext } from 'context/ProjectContext'

const Sider: React.FC = () => {
  const { projectId } = useProjectContext()
  const { fetching, sorted = [] } = usePayItemsModuleContext()
  const { globalId: payItemGlobalId, setSelected: setKey } = useSummaryContext()
  const canView = useCanView()
  const { data: counts, loading: countsLoading } =
    usePayItemSummarySiderCountsQuery({
      ...DefaultQueryHookOptions,
      variables: { projectId, input: { payItemGlobalId } },
    })
  const [disabled, setDisabled] = useState(false)
  useEffect(() => {
    setDisabled(!payItemGlobalId)
  }, [payItemGlobalId])

  const count = useCallback(
    (item: Item) => {
      const { internalConnections = [], payItemConnections = [] } =
        counts?.project?.payItemSummaryCounts ?? {}
      const internals: Map<string, number | undefined> =
        internalConnections.reduce(
          (m, v) => m.set(v.internalConnectionId, v.count),
          new Map()
        )
      const connections: Map<string, number | undefined> =
        payItemConnections.reduce(
          (m, v) => m.set(v.payItemConnection.id, v.count),
          new Map()
        )
      const groups: Map<string, number | undefined> = [
        ...payItemConnections,
        ...internalConnections,
      ].reduce((map, item) => {
        let groupId
        /**
         * we only want to add the count to the group if we can view the internal
         */
        if (
          item.__typename === 'InternalConnectionPayItemSummaryCount' &&
          canView(item.internalConnectionId)
        )
          groupId = item.groupId
        else if (item.__typename === 'PayItemConnectionPayItemSummaryCount')
          groupId = item.payItemConnection.groupId
        if (groupId)
          map.set(groupId, (map.get(groupId) ?? 0) + (item.count ?? 0))
        return map
      }, new Map())
      if (item.__typename === 'PayItemConnectionGroup')
        return groups.get(item.id)
      else if (item.__typename === 'PayItemConnection')
        return connections.get(item.id)
      else if (item.__typename === 'ProjectToInternalConnection')
        return internals.get(item.internal.id)
      return 0
    },
    [canView, counts?.project?.payItemSummaryCounts]
  )

  const menuItem = useCallback<(item: Connection | Internal) => MenuItemType>(
    (item) => {
      let label
      if (item.__typename === 'ProjectToInternalConnection')
        label = <InternalMenuItem count={count(item)} {...item} />
      else label = <ConnectionMenuItem count={count(item)} {...item} />
      return { key: JSON.stringify(item), disabled, label }
    },
    [count, disabled]
  )
  const [keys, setKeys] = useState<string[]>([])
  useEffect(() => {
    if (disabled) setKeys([])
  }, [disabled])
  return (
    <Layout.Sider
      width="fit-content"
      theme="light"
      style={{ overflowY: 'auto', overflowX: 'clip', maxHeight: '75vh' }}
    >
      <Spin spinning={(fetching ?? false) || (countsLoading ?? false)}>
        <Space direction="vertical" size="small">
          <Menu
            theme="light"
            mode="inline"
            openKeys={keys}
            onOpenChange={(keys) => setKeys(keys)}
            onClick={({ key }) => setKey(JSON.parse(key))}
            style={{ borderRight: 'unset', minWidth: 200 }}
            items={sorted.reduce((items, item) => {
              if (
                item.__typename === 'ProjectToInternalConnection' &&
                !canView(item.internal.id)
              )
                return items
              else if (item.__typename === 'PayItemConnectionGroup') {
                const { id: key, name, description } = item
                const itemCount = count(item) ?? 0
                const label = <Tooltip title={description}>{name}</Tooltip>
                const children = item.children.reduce((children, child) => {
                  if (
                    child.__typename === 'ProjectToInternalConnection' &&
                    !canView(child.internal.id)
                  )
                    return children
                  children.push(menuItem(child))
                  return children
                }, Array<MenuItemType>())
                /**
                 * don't want to render the group with no children
                 */
                if (children.length === 0) return items
                items.push({
                  key,
                  disabled,
                  label:
                    itemCount > 0 ? (
                      <CIMSBadge count={itemCount}>{label}</CIMSBadge>
                    ) : (
                      label
                    ),
                  theme: 'light',
                  children,
                })
              } else items.push(menuItem(item))
              return items
            }, Array<ItemType>())}
          />
        </Space>
      </Spin>
    </Layout.Sider>
  )
}

export default Sider
